import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { Panel, SectionTitle, TactLoader } from "../components/Tactical";
import { toast } from "sonner";
import { Loader2, Smartphone, Monitor, RefreshCw, Coins, Trophy, AlertTriangle, Gavel, Search } from "lucide-react";

/**
 * Admin view for the International Friendlies game mode.
 *
 *  - Two-pool overview (Console / Mobile) with KPIs
 *  - Top players per pool
 *  - Disputed match resolver (inline override)
 *  - Recent matches browser (filter by pool/status)
 */
export default function FriendliesAdminTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [overridePts, setOverridePts] = useState({ p1: 0, p2: 0 });

  const refresh = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const { data } = await api.get("/friendlies/admin/stats");
      setStats(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const openDispute = (m) => {
    setSelectedDispute(m);
    setOverridePts({
      p1: m.p1_submission?.my_score ?? 0,
      p2: m.p2_submission?.my_score ?? 0,
    });
  };

  const resolveDispute = async () => {
    if (!selectedDispute) return;
    try {
      await api.post(`/friendlies/admin/matches/${selectedDispute.id}/override`, {
        p1_score: parseInt(overridePts.p1, 10) || 0,
        p2_score: parseInt(overridePts.p2, 10) || 0,
      });
      toast.success("Friendly dispute resolved.");
      setSelectedDispute(null);
      refresh(true);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) return <TactLoader />;
  if (!stats) return <Panel className="font-mono text-[#71717A] text-sm">[ NO STATS AVAILABLE. ]</Panel>;

  return (
    <div className="space-y-8" data-testid="admin-friendlies-tab">
      <div className="flex items-center justify-between">
        <SectionTitle kicker="// game mode telemetry" testId="admin-friendlies-title">International Friendlies</SectionTitle>
        <button
          type="button"
          onClick={() => refresh(true)}
          disabled={refreshing}
          data-testid="admin-friendlies-refresh"
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 text-[11px] font-mono uppercase tracking-widest text-white/80 disabled:opacity-40"
        >
          {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Refresh
        </button>
      </div>

      {/* Tokens minted KPI */}
      <Panel data-testid="admin-friendlies-tokens-summary" className="flex items-center gap-4">
        <div className="p-3 rounded-full bg-[#FFD700]/15">
          <Coins className="w-5 h-5 text-[#FFD700]" />
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#71717A]">tokens redeemed from friendlies</div>
          <div className="text-2xl font-extrabold text-[#FFD700]">
            {stats.tokens?.total_minted ?? 0}
            <span className="ml-2 text-[11px] font-mono text-[#71717A]">
              by {stats.tokens?.redeemers ?? 0} player{(stats.tokens?.redeemers ?? 0) === 1 ? "" : "s"}
            </span>
          </div>
        </div>
      </Panel>

      {/* Pool grids */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {["console", "mobile"].map((pool) => (
          <PoolPanel key={pool} pool={pool} data={stats.pools?.[pool]} />
        ))}
      </div>

      {/* Disputed matches */}
      <Panel data-testid="admin-friendlies-disputed">
        <div className="flex items-center gap-2 mb-3">
          <Gavel className="w-4 h-4 text-[#E11D48]" />
          <SectionTitle kicker="// requires action" testId="admin-friendlies-disputed-title">
            Disputed Friendlies ({stats.disputed?.length || 0})
          </SectionTitle>
        </div>
        {(stats.disputed?.length || 0) === 0 ? (
          <div className="font-mono text-[#71717A] text-sm">[ NO DISPUTED FRIENDLIES. ALL CLEAR. ]</div>
        ) : (
          <div className="space-y-3">
            {stats.disputed.map((m) => (
              <DisputedRow key={m.id} m={m} onOpen={openDispute} />
            ))}
          </div>
        )}
      </Panel>

      <RecentMatchesBrowser />

      {selectedDispute && (
        <DisputeOverrideModal
          match={selectedDispute}
          values={overridePts}
          setValues={setOverridePts}
          onClose={() => setSelectedDispute(null)}
          onResolve={resolveDispute}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
function PoolPanel({ pool, data }) {
  if (!data) return null;
  const Icon = pool === "console" ? Monitor : Smartphone;
  const accent = pool === "console" ? "#60A5FA" : "#00FF87";
  return (
    <Panel data-testid={`admin-friendlies-pool-${pool}`}>
      <div className="flex items-center gap-2 mb-4">
        <div className="p-2 rounded-full" style={{ backgroundColor: `${accent}20` }}>
          <Icon className="w-4 h-4" style={{ color: accent }} />
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#71717A]">pool</div>
          <h3 className="font-heading text-lg uppercase" style={{ color: accent }}>{pool}</h3>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <KPI label="Active" value={data.active_revealers} />
        <KPI label="Pending Invites" value={data.pending_invites} />
        <KPI label="In Progress" value={data.in_progress} />
        <KPI label="Confirmed" value={data.confirmed} tone="text-[#00FF87]" />
        <KPI label="Disputed" value={data.disputed} tone="text-[#E11D48]" />
        <KPI label="Pts Awarded" value={data.total_points_awarded} tone="text-[#FFD700]" />
      </div>

      {/* Top players */}
      <div className="mt-5">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#71717A] mb-2 flex items-center gap-1">
          <Trophy className="w-3 h-3" /> Top 10 players
        </div>
        {(data.top_players?.length || 0) === 0 ? (
          <div className="font-mono text-[11px] text-[#71717A]">[ no completed matches yet ]</div>
        ) : (
          <div className="space-y-1">
            {data.top_players.map((p) => (
              <div key={p.id} className="grid grid-cols-12 gap-1 text-[12px] items-center" data-testid={`admin-fr-top-${pool}-${p.id}`}>
                <div className="col-span-1 font-mono text-[#71717A]">{p.rank}</div>
                <div className="col-span-7 flex items-center gap-1.5 min-w-0">
                  <span className="text-sm">{p.current_team?.flag || "🏳️"}</span>
                  <span className="text-white truncate">@{p.username}</span>
                </div>
                <div className="col-span-1 text-center text-white/60">{p.games_played}</div>
                <div className="col-span-1 text-center text-[#00FF87]">{p.wins}</div>
                <div className="col-span-1 text-center text-[#71717A]">{p.draws}</div>
                <div className="col-span-1 text-right font-extrabold text-white">{p.points_total}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Panel>
  );
}

function KPI({ label, value, tone = "text-white" }) {
  return (
    <div className="rounded-xl bg-white/5 ring-1 ring-white/10 p-3 text-center">
      <div className={`text-xl font-extrabold ${tone}`}>{value || 0}</div>
      <div className="text-[9px] font-mono uppercase tracking-widest text-[#71717A] mt-0.5">{label}</div>
    </div>
  );
}

function DisputedRow({ m, onOpen }) {
  const p1Sub = m.p1_submission || null;
  const p2Sub = m.p2_submission || null;
  return (
    <div className="grid grid-cols-12 gap-2 items-center rounded-xl bg-white/5 ring-1 ring-white/10 p-3" data-testid={`admin-fr-disputed-${m.id}`}>
      <div className="col-span-1 font-mono text-[10px] uppercase text-[#71717A]">{m.platform || "?"}</div>
      <div className="col-span-3 text-white text-sm truncate">@{m.p1_user?.username || m.p1_id?.slice(0, 6)}</div>
      <div className="col-span-2 text-center font-mono text-[11px] text-white/80">
        {p1Sub ? `${p1Sub.my_score}–${p1Sub.opp_score}` : <span className="text-[#71717A]">none</span>}
      </div>
      <div className="col-span-1 text-center text-[#71717A] text-xs">vs</div>
      <div className="col-span-2 text-center font-mono text-[11px] text-white/80">
        {p2Sub ? `${p2Sub.my_score}–${p2Sub.opp_score}` : <span className="text-[#71717A]">none</span>}
      </div>
      <div className="col-span-2 text-right text-white text-sm truncate">@{m.p2_user?.username || m.p2_id?.slice(0, 6)}</div>
      <div className="col-span-1">
        <button
          type="button"
          onClick={() => onOpen(m)}
          data-testid={`admin-fr-resolve-${m.id}`}
          className="w-full inline-flex items-center justify-center gap-1 px-2 py-1 rounded-full bg-[#E11D48] text-white text-[10px] font-extrabold uppercase tracking-widest"
        >
          <Gavel className="w-3 h-3" />Resolve
        </button>
      </div>
    </div>
  );
}

function DisputeOverrideModal({ match, values, setValues, onClose, onResolve }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="admin-fr-dispute-modal">
      <div className="bg-[#0F0F12] border border-[#2A2A30] rounded-2xl max-w-md w-full p-6 space-y-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-[#E11D48]" />
          <h3 className="font-heading text-lg uppercase text-white">Resolve Friendly Dispute</h3>
        </div>
        <div className="text-[11px] font-mono text-[#71717A]">
          {match.platform?.toUpperCase()} POOL · MATCH ID {match.id?.slice(0, 8)}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#71717A] mb-1">
              @{match.p1_user?.username} ({match.p1_team?.flag} {match.p1_team?.code})
            </div>
            <input
              type="number" min="0" max="99"
              value={values.p1}
              onChange={(e) => setValues({ ...values, p1: e.target.value })}
              data-testid="admin-fr-override-p1"
              className="w-full px-3 py-2 rounded-lg bg-white/5 ring-1 ring-white/10 text-center text-2xl font-extrabold text-white focus:ring-[#E11D48] outline-none"
            />
            <div className="text-[9px] font-mono text-[#71717A] mt-1">
              submitted: {match.p1_submission ? `${match.p1_submission.my_score}–${match.p1_submission.opp_score}` : "none"}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#71717A] mb-1">
              @{match.p2_user?.username} ({match.p2_team?.flag} {match.p2_team?.code})
            </div>
            <input
              type="number" min="0" max="99"
              value={values.p2}
              onChange={(e) => setValues({ ...values, p2: e.target.value })}
              data-testid="admin-fr-override-p2"
              className="w-full px-3 py-2 rounded-lg bg-white/5 ring-1 ring-white/10 text-center text-2xl font-extrabold text-white focus:ring-[#E11D48] outline-none"
            />
            <div className="text-[9px] font-mono text-[#71717A] mt-1">
              submitted: {match.p2_submission ? `${match.p2_submission.my_score}–${match.p2_submission.opp_score}` : "none"}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-2 rounded-full bg-white/5 hover:bg-white/10 text-[11px] font-mono uppercase tracking-widest text-white/70" data-testid="admin-fr-override-cancel">Cancel</button>
          <button type="button" onClick={onResolve} className="px-4 py-2 rounded-full bg-[#E11D48] text-white text-[11px] font-extrabold uppercase tracking-widest" data-testid="admin-fr-override-save">Confirm Override</button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
function RecentMatchesBrowser() {
  const [rows, setRows] = useState(null);
  const [poolFilter, setPoolFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = useCallback(async () => {
    setRows(null);
    try {
      const { data } = await api.get("/friendlies/admin/matches", {
        params: { platform: poolFilter || undefined, status: statusFilter || undefined, limit: 100 },
      });
      setRows(data.matches || []);
    } catch { setRows([]); }
  }, [poolFilter, statusFilter]);
  useEffect(() => { load(); }, [load]);

  return (
    <Panel data-testid="admin-friendlies-matches">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <SectionTitle kicker="// recent matches" testId="admin-friendlies-matches-title">All Friendlies</SectionTitle>
        <div className="flex gap-2">
          <FilterChip label="All pools" value="" current={poolFilter} setCurrent={setPoolFilter} />
          <FilterChip label="Console"   value="console" current={poolFilter} setCurrent={setPoolFilter} />
          <FilterChip label="Mobile"    value="mobile"  current={poolFilter} setCurrent={setPoolFilter} />
          <span className="w-px h-6 bg-white/10" />
          <FilterChip label="All status" value="" current={statusFilter} setCurrent={setStatusFilter} />
          <FilterChip label="In progress" value="in_progress" current={statusFilter} setCurrent={setStatusFilter} />
          <FilterChip label="Confirmed"  value="confirmed" current={statusFilter} setCurrent={setStatusFilter} />
          <FilterChip label="Disputed"   value="disputed"  current={statusFilter} setCurrent={setStatusFilter} />
        </div>
      </div>
      {rows === null ? <TactLoader /> :
       rows.length === 0 ? <div className="font-mono text-[#71717A] text-sm">[ NO MATCHES MATCH FILTERS ]</div> : (
        <div className="space-y-1">
          {rows.map((m) => (
            <div key={m.id} className="grid grid-cols-12 gap-1 text-[12px] items-center rounded-lg bg-white/5 hover:bg-white/10 transition-colors p-2" data-testid={`admin-fr-match-${m.id}`}>
              <div className="col-span-1 font-mono text-[10px] uppercase text-[#71717A]">{m.platform}</div>
              <div className="col-span-3 truncate text-white">
                <span className="text-sm">{m.p1_team?.flag}</span> @{m.p1_user?.username || m.p1_id?.slice(0, 6)}
              </div>
              <div className="col-span-2 text-center font-extrabold text-white">
                {m.status === "confirmed"
                  ? `${m.confirmed_score_p1}–${m.confirmed_score_p2}`
                  : m.status === "in_progress" ? <span className="text-[#71717A]">·–·</span>
                  : <span className="text-[#E11D48]">⚠</span>}
              </div>
              <div className="col-span-3 truncate text-white text-right">
                @{m.p2_user?.username || m.p2_id?.slice(0, 6)} <span className="text-sm">{m.p2_team?.flag}</span>
              </div>
              <div className={`col-span-2 text-right font-mono text-[10px] uppercase ${
                m.status === "confirmed" ? "text-[#00FF87]" :
                m.status === "disputed"  ? "text-[#E11D48]" :
                "text-[#FFD700]"
              }`}>
                {m.status}{m.admin_override ? " · override" : ""}
              </div>
              <div className="col-span-1 text-right font-mono text-[9px] text-[#71717A]">
                {(m.created_at || "").slice(5, 10)}
              </div>
            </div>
          ))}
        </div>
       )}
    </Panel>
  );
}

function FilterChip({ label, value, current, setCurrent }) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => setCurrent(value)}
      data-testid={`admin-fr-filter-${value || "all"}-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest transition ${
        active ? "bg-[#00FF87] text-[#06120A]" : "bg-white/5 text-white/60 hover:bg-white/10"
      }`}
    >
      {label}
    </button>
  );
}
